import booleanContains from '@turf/boolean-contains';
import bboxPolygon from '@turf/bbox-polygon';
import area from '@turf/area';
import { polygon } from '@turf/helpers';

const convertSnakeCase = (str) => {
  if (!str) return str;
  // resolve numerical ranges:
  let i, j, x;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    i = str.search(/\d+_\d+/g);
    if (i < 0) break;
    for (j = i; j < str.length; j++) {
      if (str[j] === '_') {
        str = str.substr(0, j) + '-' + str.substr(j + 1);
        break;
      }
    }
  }
  str = str.split('_').filter((d) => d.length > 0);

  for (i = 0, x = str.length; i < x; i++) {
    str[i] = str[i][0].toUpperCase() + str[i].substr(1);
  }

  return str.join(' ');
};

const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
    .replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    })
    .toUpperCase();
};

const uniqueId = () => {
  return (
    'Uxxxxxxxxxxxx'.replace(/x/g, () => {
      const r = (Math.random() * 16) | 0;
      return r.toString(16);
    }) + `_${new Date().getTime()}`
  ).toUpperCase();
};

const arrayNotEmpty = (a) => {
  return Array.isArray(a) && a.length > 0;
};

const polygonPercentWithin = (poly, bbox) => {
  const p = polygon([[poly.nw, poly.ne, poly.se, poly.sw, poly.nw]]);
  const b = bboxPolygon([bbox.minx, bbox.miny, bbox.maxx, bbox.maxy]);

  if (!booleanContains(b, p)) {
    return false;
  }

  const pArea = area(p);
  const bArea = area(b);
  return pArea / bArea;
};

const bboxPercentWithin = (prev, cur) => {
  const pPoly = bboxPolygon([prev.minx, prev.miny, prev.maxx, prev.maxy]);
  const cPoly = bboxPolygon([cur.minx, cur.miny, cur.maxx, cur.maxy]);

  if (!booleanContains(pPoly, cPoly)) {
    return false;
  }

  const pArea = area(pPoly);
  const cArea = area(cPoly);
  return cArea / pArea;
};

const middleTrunc = (fullStr, strLen, separator) => {
  if (fullStr.length <= strLen) return fullStr;

  separator = separator || '...';

  const sepLen = separator.length,
    charsToShow = strLen - sepLen,
    frontChars = Math.ceil(charsToShow / 2),
    backChars = Math.floor(charsToShow / 2);

  return fullStr.substr(0, frontChars) + separator + fullStr.substr(fullStr.length - backChars);
};

const stopWords = [
  'i',
  'me',
  'my',
  'myself',
  'we',
  'our',
  'ours',
  'ourselves',
  'you',
  "you're",
  "you've",
  "you'll",
  "you'd",
  'your',
  'yours',
  'yourself',
  'yourselves',
  'he',
  'him',
  'his',
  'himself',
  'she',
  "she's",
  'her',
  'hers',
  'herself',
  'it',
  "it's",
  'its',
  'itself',
  'they',
  'them',
  'their',
  'theirs',
  'themselves',
  'what',
  'which',
  'who',
  'whom',
  'this',
  'that',
  "that'll",
  'these',
  'those',
  'am',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'have',
  'has',
  'had',
  'having',
  'do',
  'does',
  'did',
  'doing',
  'a',
  'an',
  'the',
  'and',
  'but',
  'if',
  'or',
  'because',
  'as',
  'until',
  'while',
  'of',
  'at',
  'by',
  'for',
  'with',
  'about',
  'against',
  'between',
  'into',
  'through',
  'during',
  'before',
  'after',
  'above',
  'below',
  'to',
  'from',
  'up',
  'down',
  'in',
  'out',
  'on',
  'off',
  'over',
  'under',
  'again',
  'further',
  'then',
  'once',
  'here',
  'there',
  'when',
  'where',
  'why',
  'how',
  'all',
  'any',
  'both',
  'each',
  'few',
  'more',
  'most',
  'other',
  'some',
  'such',
  'no',
  'nor',
  'not',
  'only',
  'own',
  'same',
  'so',
  'than',
  'too',
  'very',
  's',
  't',
  'can',
  'will',
  'just',
  'don',
  "don't",
  'should',
  "should've",
  'now',
  'd',
  'll',
  'm',
  'o',
  're',
  've',
  'y',
  'ain',
  'aren',
  "aren't",
  'couldn',
  "couldn't",
  'didn',
  "didn't",
  'doesn',
  "doesn't",
  'hadn',
  "hadn't",
  'hasn',
  "hasn't",
  'haven',
  "haven't",
  'isn',
  "isn't",
  'ma',
  'mightn',
  "mightn't",
  'mustn',
  "mustn't",
  'needn',
  "needn't",
  'shan',
  "shan't",
  'shouldn',
  "shouldn't",
  'wasn',
  "wasn't",
  'weren',
  "weren't",
  'won',
  "won't",
  'wouldn',
  "wouldn't"
];

export { convertSnakeCase, uniqueId, arrayNotEmpty, uuidv4, bboxPercentWithin, polygonPercentWithin, stopWords, middleTrunc };
